import React, { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import {
  ShoppingCart,
  DollarSign,
  PackageCheck,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Calculator,
  Warehouse,
  Users,
  CreditCard,
  BarChart3
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import { formatDateForInput, createDateRange, parseDate } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import type { DateRange } from '@/types'

interface PurchaseOrder {
  _id: string
  poNumber: string
  supplierName: string
  orderDate: string
  items: any[]
  amount: number
  receivedAmount: number
  paidAmount: number
  status: string
}

interface SupplierBreakdown {
  supplierName: string
  totalOrders: number
  totalAmount: number
  totalReceived: number
  pendingCredits: number
}

interface TopPurchasedItem {
  name: string
  unit: string
  totalQuantity: number
  totalSpend: number
  avgUnitPrice: number
  orderCount: number
}

interface AgingCredit {
  supplierName: string
  totalAmount: number
  amountPaid: number
  balance: number
  status: string
  daysOutstanding: number
  agingBucket: 'current' | 'warning' | 'overdue'
  createdAt: string
}

interface DiscrepancySummary {
  totalReceivings: number
  discrepancyCount: number
  discrepancyRate: number
  supplierDiscrepancies: { supplierName: string; count: number; items: string[] }[]
}

interface OrderAnalysis {
  totalOrders: number
  plannedOrders: number
  quickOrders: number
  dailyPOs: number
  quickOrderPercentage: number
}

interface PaymentTimelineEntry {
  supplierName: string
  type: string
  amount: number
  note: string
  balanceAfter: number
  date: string
}

interface PurchaseReportData {
  summary: {
    totalPurchaseOrders: number
    totalAmountSpent: number
    totalReceived: number
    totalPendingCredits: number
  }
  purchaseOrders: PurchaseOrder[]
  supplierBreakdown: SupplierBreakdown[]
  topPurchasedItems: TopPurchasedItem[]
  creditAging: {
    credits: AgingCredit[]
    summary: { currentTotal: number; warningTotal: number; overdueTotal: number }
  }
  discrepancySummary: DiscrepancySummary
  orderAnalysis: OrderAnalysis
  paymentTimeline: PaymentTimelineEntry[]
}

export default function PurchaseReportScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [reportData, setReportData] = useState<PurchaseReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [activeTab, setActiveTab] = useState<'orders' | 'suppliers'>('orders')

  const loadReport = async () => {
    if (!currentRestaurant?._id) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ApiService.getPurchaseReport(currentRestaurant._id, dateRange)
      if (response.success) {
        setReportData(response.data)
      } else {
        setError(response.message || 'Failed to load purchase report')
      }
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to load purchase report'
        : 'Failed to load purchase report'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) {
      loadReport()
    }
  }, [currentRestaurant?._id])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    setDateRange(prev => {
      const newDate = parseDate(value)
      return field === 'from'
        ? createDateRange(newDate, prev.to)
        : createDateRange(prev.from, newDate)
    })
  }

  const getStatusBadge = (status: string) => {
    const s = status.toLowerCase()
    if (s === 'completed' || s === 'received' || s === 'delivered') {
      return <Badge variant="default" className="bg-green-600 text-xs">{status}</Badge>
    }
    if (s === 'pending' || s === 'ordered') {
      return <Badge variant="secondary" className="text-xs">{status}</Badge>
    }
    if (s === 'cancelled' || s === 'rejected') {
      return <Badge variant="destructive" className="text-xs">{status}</Badge>
    }
    return <Badge variant="outline" className="text-xs">{status}</Badge>
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading purchase report...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button
              variant="outline"
              size="sm"
              className="ml-2"
              onClick={loadReport}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!reportData) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No purchase report data available for the selected period.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const { summary, purchaseOrders, supplierBreakdown, topPurchasedItems, creditAging, discrepancySummary, orderAnalysis, paymentTimeline } = reportData

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Purchase Report</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Purchase orders, receivings & supplier analysis
          </p>
        </div>
        <Button onClick={loadReport} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Date Range Selector - Desktop */}
      <div className="hidden md:flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1">
          <DateRangeModal
            dateRange={dateRange}
            onDateChange={handleDateChange}
            onApply={loadReport}
            title="Purchase Report Period"
            description="Select the date range for your purchase report"
          />
        </div>
        <Button
          onClick={loadReport}
          className="sm:w-auto flex items-center justify-center gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <Calculator className="h-4 w-4" />
              Apply
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total POs</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalPurchaseOrders}</div>
            <p className="text-xs text-muted-foreground">Purchase orders</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.totalAmountSpent)}</div>
            <p className="text-xs text-muted-foreground">Amount spent</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
            <PackageCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.totalReceived)}</div>
            <p className="text-xs text-muted-foreground">Amount paid to suppliers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Credits</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-orange-600">{formatCurrency(summary.totalPendingCredits)}</div>
            <p className="text-xs text-muted-foreground">Unsettled credits</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 border-b">
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'orders'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <ShoppingCart className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
          Purchase Orders
        </button>
        <button
          onClick={() => setActiveTab('suppliers')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'suppliers'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
          Supplier Breakdown
        </button>
      </div>

      {/* Purchase Orders Tab */}
      {activeTab === 'orders' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ShoppingCart className="h-5 w-5" />
              <span>Purchase Orders</span>
            </CardTitle>
            <CardDescription>
              {purchaseOrders.length} purchase order{purchaseOrders.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {purchaseOrders.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No purchase orders for the selected period.</p>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium">PO Number</th>
                        <th className="text-left py-3 px-2 font-medium">Supplier</th>
                        <th className="text-left py-3 px-2 font-medium">Date</th>
                        <th className="text-right py-3 px-2 font-medium">Items</th>
                        <th className="text-right py-3 px-2 font-medium">Amount</th>
                        <th className="text-right py-3 px-2 font-medium">Paid</th>
                        <th className="text-center py-3 px-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrders.map((po) => (
                        <tr key={po._id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-2 font-mono text-xs">{po.poNumber}</td>
                          <td className="py-3 px-2">{po.supplierName}</td>
                          <td className="py-3 px-2">{po.orderDate ? new Date(po.orderDate).toLocaleDateString() : '-'}</td>
                          <td className="py-3 px-2 text-right">{po.items?.length || 0}</td>
                          <td className="py-3 px-2 text-right font-medium">{formatCurrency(po.amount)}</td>
                          <td className="py-3 px-2 text-right">{formatCurrency(po.paidAmount)}</td>
                          <td className="py-3 px-2 text-center">{getStatusBadge(po.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3">
                  {purchaseOrders.map((po) => (
                    <div key={po._id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-medium">{po.poNumber}</span>
                        {getStatusBadge(po.status)}
                      </div>
                      <div className="text-sm font-medium">{po.supplierName}</div>
                      <div className="text-xs text-muted-foreground">
                        {po.orderDate ? new Date(po.orderDate).toLocaleDateString() : '-'} &bull; {po.items?.length || 0} items
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                        <div>
                          <div className="text-muted-foreground">Amount</div>
                          <div className="font-medium">{formatCurrency(po.amount)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Paid</div>
                          <div className="font-medium">{formatCurrency(po.paidAmount)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Supplier Breakdown Tab */}
      {activeTab === 'suppliers' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>Supplier Breakdown</span>
            </CardTitle>
            <CardDescription>
              Ranked by total purchase amount
            </CardDescription>
          </CardHeader>
          <CardContent>
            {supplierBreakdown.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No supplier data available.</p>
            ) : (
              <div className="space-y-3">
                {supplierBreakdown.map((supplier, index) => (
                  <div key={supplier.supplierName} className="border rounded-lg p-3 sm:p-4">
                    {/* Mobile Layout */}
                    <div className="block lg:hidden space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm sm:text-base">{supplier.supplierName}</div>
                          <div className="text-xs text-muted-foreground">{supplier.totalOrders} order{supplier.totalOrders !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div>
                          <div className="font-medium">{formatCurrency(supplier.totalAmount)}</div>
                          <div className="text-muted-foreground">Amount</div>
                        </div>
                        <div>
                          <div className="font-medium">{formatCurrency(supplier.totalReceived)}</div>
                          <div className="text-muted-foreground">Received</div>
                        </div>
                        <div>
                          <div className={`font-medium ${supplier.pendingCredits > 0 ? 'text-orange-600' : ''}`}>
                            {formatCurrency(supplier.pendingCredits)}
                          </div>
                          <div className="text-muted-foreground">Credits</div>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden lg:flex lg:items-center lg:justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{supplier.supplierName}</div>
                          <div className="text-sm text-muted-foreground">{supplier.totalOrders} order{supplier.totalOrders !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-6 text-right text-sm">
                        <div>
                          <div className="font-medium">{formatCurrency(supplier.totalAmount)}</div>
                          <div className="text-muted-foreground">Amount</div>
                        </div>
                        <div>
                          <div className="font-medium">{formatCurrency(supplier.totalReceived)}</div>
                          <div className="text-muted-foreground">Received</div>
                        </div>
                        <div>
                          <div className={`font-medium ${supplier.pendingCredits > 0 ? 'text-orange-600' : ''}`}>
                            {formatCurrency(supplier.pendingCredits)}
                          </div>
                          <div className="text-muted-foreground">Credits</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Purchased Items */}
      <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ShoppingCart className="h-5 w-5" />
              <span>Top Purchased Items</span>
            </CardTitle>
            <CardDescription>
              Top 10 items ranked by total spend from receivings
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!topPurchasedItems || topPurchasedItems.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No item data available from receivings.</p>
            ) : (
              <div className="space-y-3">
                {topPurchasedItems.map((item, index) => (
                  <div key={item.name} className="border rounded-lg p-3 sm:p-4">
                    {/* Mobile Layout */}
                    <div className="block lg:hidden space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm sm:text-base">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.unit || 'unit'} &bull; {item.orderCount} receiving{item.orderCount !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div>
                          <div className="font-medium">{item.totalQuantity.toFixed(1)}</div>
                          <div className="text-muted-foreground">Qty</div>
                        </div>
                        <div>
                          <div className="font-medium">{formatCurrency(item.totalSpend)}</div>
                          <div className="text-muted-foreground">Spend</div>
                        </div>
                        <div>
                          <div className="font-medium">{formatCurrency(item.avgUnitPrice)}</div>
                          <div className="text-muted-foreground">Avg Price</div>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden lg:flex lg:items-center lg:justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center text-white text-xs font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{item.name}</div>
                          <div className="text-sm text-muted-foreground">{item.unit || 'unit'} &bull; {item.orderCount} receiving{item.orderCount !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-6 text-right text-sm">
                        <div>
                          <div className="font-medium">{item.totalQuantity.toFixed(1)}</div>
                          <div className="text-muted-foreground">Total Qty</div>
                        </div>
                        <div>
                          <div className="font-medium">{formatCurrency(item.totalSpend)}</div>
                          <div className="text-muted-foreground">Total Spend</div>
                        </div>
                        <div>
                          <div className="font-medium">{formatCurrency(item.avgUnitPrice)}</div>
                          <div className="text-muted-foreground">Avg Unit Price</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>


      {/* Credits & Payments */}
      <div className="space-y-4">
        {/* Credit Aging Summary */}
          <div className="grid gap-2 sm:gap-4 grid-cols-3">
            <Card className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-900">
              <CardContent className="pt-4 pb-3 px-3 sm:px-6">
                <div className="text-xs sm:text-sm text-green-700 dark:text-green-400 font-medium">Current (0-7d)</div>
                <div className="text-lg sm:text-2xl font-bold text-green-700 dark:text-green-400">{formatCurrency(creditAging?.summary?.currentTotal || 0)}</div>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-900">
              <CardContent className="pt-4 pb-3 px-3 sm:px-6">
                <div className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-400 font-medium">Warning (8-30d)</div>
                <div className="text-lg sm:text-2xl font-bold text-yellow-700 dark:text-yellow-400">{formatCurrency(creditAging?.summary?.warningTotal || 0)}</div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900">
              <CardContent className="pt-4 pb-3 px-3 sm:px-6">
                <div className="text-xs sm:text-sm text-red-700 dark:text-red-400 font-medium">Overdue (30d+)</div>
                <div className="text-lg sm:text-2xl font-bold text-red-700 dark:text-red-400">{formatCurrency(creditAging?.summary?.overdueTotal || 0)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Outstanding Credits List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5" />
                <span>Outstanding Credits</span>
              </CardTitle>
              <CardDescription>
                {creditAging?.credits?.length || 0} outstanding credit{(creditAging?.credits?.length || 0) !== 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!creditAging?.credits || creditAging.credits.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No outstanding credits.</p>
              ) : (
                <div className="space-y-3">
                  {creditAging.credits.map((credit, index) => (
                    <div key={index} className="border rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <Badge className={
                          credit.agingBucket === 'current' ? 'bg-green-600 text-xs' :
                          credit.agingBucket === 'warning' ? 'bg-yellow-600 text-xs' :
                          'bg-red-600 text-xs'
                        }>
                          {credit.daysOutstanding}d
                        </Badge>
                        <div>
                          <div className="font-medium text-sm">{credit.supplierName || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground">
                            {credit.createdAt ? new Date(credit.createdAt).toLocaleDateString() : '-'} &bull; {credit.status}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-4 text-sm pl-10 sm:pl-0">
                        <div>
                          <div className="text-xs text-muted-foreground">Total</div>
                          <div className="font-medium">{formatCurrency(credit.totalAmount)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Paid</div>
                          <div className="font-medium">{formatCurrency(credit.amountPaid)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Balance</div>
                          <div className="font-medium text-orange-600">{formatCurrency(credit.balance)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5" />
                <span>Recent Payment History</span>
              </CardTitle>
              <CardDescription>
                Last {paymentTimeline?.length || 0} payment activities
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!paymentTimeline || paymentTimeline.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No payment history available.</p>
              ) : (
                <div className="space-y-2">
                  {paymentTimeline.map((entry, index) => (
                    <div key={index} className="flex items-center gap-3 border-b last:border-b-0 py-2">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        entry.type === 'payment' ? 'bg-green-500' : entry.type === 'credit' ? 'bg-blue-500' : 'bg-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{entry.supplierName || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground truncate">{entry.note || entry.type}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-medium">{formatCurrency(entry.amount)}</div>
                        <div className="text-xs text-muted-foreground">{entry.date ? new Date(entry.date).toLocaleDateString() : '-'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
      </div>

      {/* Insights */}
      <div className="space-y-4">
        {/* Order Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Order Analysis</span>
              </CardTitle>
              <CardDescription>Breakdown of order types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{orderAnalysis?.totalOrders || 0}</div>
                  <div className="text-xs text-blue-600 dark:text-blue-400">Total Orders</div>
                </div>
                <div className="rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 p-3 text-center">
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">{orderAnalysis?.plannedOrders || 0}</div>
                  <div className="text-xs text-green-600 dark:text-green-400">Planned</div>
                </div>
                <div className="rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 p-3 text-center">
                  <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">{orderAnalysis?.quickOrders || 0}</div>
                  <div className="text-xs text-orange-600 dark:text-orange-400">Quick Orders ({orderAnalysis?.quickOrderPercentage || 0}%)</div>
                </div>
                <div className="rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900 p-3 text-center">
                  <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">{orderAnalysis?.dailyPOs || 0}</div>
                  <div className="text-xs text-purple-600 dark:text-purple-400">Daily POs</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Discrepancies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5" />
                <span>Delivery Discrepancies</span>
              </CardTitle>
              <CardDescription>
                {discrepancySummary?.discrepancyCount || 0} discrepancies out of {discrepancySummary?.totalReceivings || 0} receivings ({discrepancySummary?.discrepancyRate || 0}%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Discrepancy Rate Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Discrepancy Rate</span>
                  <span className="font-medium">{discrepancySummary?.discrepancyRate || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${
                      (discrepancySummary?.discrepancyRate || 0) > 20 ? 'bg-red-500' :
                      (discrepancySummary?.discrepancyRate || 0) > 10 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(discrepancySummary?.discrepancyRate || 0, 100)}%` }}
                  />
                </div>
              </div>

              {/* Per-supplier discrepancies */}
              {!discrepancySummary?.supplierDiscrepancies || discrepancySummary.supplierDiscrepancies.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No discrepancies recorded.</p>
              ) : (
                <div className="space-y-3">
                  {discrepancySummary.supplierDiscrepancies.map((sd, index) => (
                    <div key={index} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{sd.supplierName}</span>
                        <Badge variant="destructive" className="text-xs">{sd.count} issue{sd.count !== 1 ? 's' : ''}</Badge>
                      </div>
                      {sd.items.length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Items: {sd.items.slice(0, 5).join(', ')}{sd.items.length > 5 ? ` +${sd.items.length - 5} more` : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
      </div>

      {/* Mobile Floating Filter */}
      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={loadReport}
        title="Purchase Report Date Range"
        description="Select the date range for your purchase report"
      />
    </div>
  )
}
